// ===================================================================
// PA GRANTS — Applicants (Organizations)
// Full CRUD with multi-tab form, search/filter, status badges
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Textarea, Modal, Tabs, SimpleGrid, Checkbox, ActionIcon, Loader, Center,
  Table,
} from '../../components/gab/GabComponents';
import { IconPlus, IconEdit, IconTrash, IconSearch, IconDownload, IconUpload } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { exportToCSV } from '../../lib/exportUtils';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useDisasterFilter } from '../../hooks/useActiveDisaster';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { PAOrganization } from '../../types/pagrants';

// ── Constants ────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  active: 'green', inactive: 'gray', pending: 'yellow',
};

const ROLE_COLORS: Record<string, string> = {
  grantee: 'blue', subgrantee: 'violet', applicant: 'teal',
};

const ORG_TYPE_OPTIONS = [
  { value: 'state', label: 'State' },
  { value: 'county', label: 'County' },
  { value: 'city', label: 'City' },
  { value: 'town', label: 'Town' },
  { value: 'township', label: 'Township' },
  { value: 'district', label: 'District' },
  { value: 'tribal', label: 'Tribal' },
  { value: 'pnp', label: 'Private Non-Profit (PNP)' },
];

const ORG_ROLE_OPTIONS = [
  { value: 'grantee', label: 'Grantee' },
  { value: 'subgrantee', label: 'Subgrantee' },
  { value: 'applicant', label: 'Applicant' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
];

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const BANK_ACCOUNT_TYPE_OPTIONS = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
];

const INDIRECT_COST_RATE_TYPE_OPTIONS = [
  { value: 'negotiated', label: 'Negotiated' },
  { value: 'de_minimis', label: 'De Minimis (10%)' },
  { value: 'none', label: 'None' },
];

const FILTER_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  ...ORG_TYPE_OPTIONS,
];

const FILTER_ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  ...ORG_ROLE_OPTIONS,
];

// ── Empty form state ─────────────────────────────────────────────

type FormState = Omit<PAOrganization, 'id' | 'Rid'>;

function emptyForm(): FormState {
  return {
    orgName: '', orgType: '', orgRole: '', parentOrgId: '',
    fipsCode: '', femaApplicantId: '', ueiNumber: '',
    samRegistered: '', samExpirationDate: '', einTaxId: '', congressionalDistrict: '',
    physicalAddress: '', physicalCity: '', physicalState: '', physicalZip: '', physicalCounty: '',
    mailingAddress: '', mailingCity: '', mailingState: '', mailingZip: '',
    primaryContact: '', primaryTitle: '', primaryEmail: '', primaryPhone: '',
    altContact: '', altTitle: '', altEmail: '', altPhone: '',
    garName: '', garEmail: '', garPhone: '',
    scoName: '', spaoName: '', spaoEmail: '', contactEmails: '',
    pnpType: '', pnpTaxExemptVerified: '', pnpTaxExemptLetter: '',
    pnpOpenToPublic: '', pnpServicesDescription: '', pnpCriticalFacility: '',
    procurementPolicyOnFile: '', forceAccountPolicyOnFile: '',
    indirectCostRate: '', indirectCostRateType: '',
    singleAuditRequired: '', singleAuditCurrent: '',
    bankName: '', bankRoutingNumber: '', bankAccountNumber: '', bankAccountType: '',
    status: 'active', notes: '',
  };
}

function formFromOrg(org: PAOrganization): FormState {
  const { id: _id, Rid: _rid, ...rest } = org;
  return rest;
}

// ── Component ────────────────────────────────────────────────────

export default function Applicants() {
  const { canEdit } = useModulePermission('pagrants');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Detect if we're under /pagrants/hmgp or /pagrants/pa
  const routeBase = pathname.includes('/pagrants/hmgp') ? '/pagrants/hmgp' : '/pagrants/pa';
  const {
    organizations, projects: allProjects, documents, loadingOrgs,
    fetchAll,
    createOrg, updateOrg, deleteOrg,
  } = usePAGrantsStore();
  const allFilteredProjects = useDisasterFilter(allProjects);
  // Filter by program context: PA shows only PA projects, HMGP shows only HMGP
  const projects = routeBase.includes('hmgp')
    ? allFilteredProjects.filter(p => p.workType === 'hmgp')
    : allFilteredProjects.filter(p => p.workType !== 'hmgp');

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Pagination
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<PAOrganization | null>(null);
  const [deletingOrg, setDeletingOrg] = useState<PAOrganization | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [sameAsPhysical, setSameAsPhysical] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('basic');

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Filtered list ──────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = organizations;
    if (typeFilter !== 'all') list = list.filter(o => o.orgType === typeFilter);
    if (roleFilter !== 'all') list = list.filter(o => o.orgRole === roleFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        (o.orgName || '').toLowerCase().includes(q) ||
        (o.femaApplicantId || '').toLowerCase().includes(q) ||
        (o.ueiNumber || '').toLowerCase().includes(q) ||
        (o.primaryContact || '').toLowerCase().includes(q) ||
        (o.physicalCity || '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (a.orgName || '').localeCompare(b.orgName || '');
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const numA = Number(va); const numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB) && va !== '' && vb !== '') return (numA - numB) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [organizations, typeFilter, roleFilter, search, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [filtered.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Form helpers ───────────────────────────────────────────────

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function openCreate() {
    setEditingOrg(null);
    setForm(emptyForm());
    setSameAsPhysical(false);
    setActiveTab('basic');
    setModalOpen(true);
  }

  function openEdit(org: PAOrganization) {
    setEditingOrg(org);
    setForm(formFromOrg(org));
    setSameAsPhysical(
      org.mailingAddress === org.physicalAddress &&
      org.mailingCity === org.physicalCity &&
      org.mailingState === org.physicalState &&
      org.mailingZip === org.physicalZip &&
      org.physicalAddress !== '',
    );
    setActiveTab('basic');
    setModalOpen(true);
  }

  function openDelete(org: PAOrganization) {
    setDeletingOrg(org);
    setDeleteOpen(true);
  }

  // Sync mailing address when "same as physical" is checked
  useEffect(() => {
    if (sameAsPhysical) {
      setForm(prev => ({
        ...prev,
        mailingAddress: prev.physicalAddress,
        mailingCity: prev.physicalCity,
        mailingState: prev.physicalState,
        mailingZip: prev.physicalZip,
      }));
    }
  }, [sameAsPhysical, form.physicalAddress, form.physicalCity, form.physicalState, form.physicalZip]);

  async function handleSave() {
    setSaving(true);
    try {
      if (editingOrg) {
        await updateOrg(editingOrg.Rid, form);
      } else {
        await createOrg(form);
      }
      setModalOpen(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deletingOrg) return;
    setDeleting(true);
    try {
      await deleteOrg(deletingOrg.Rid);
      setDeleteOpen(false);
      setDeletingOrg(null);
    } finally {
      setDeleting(false);
    }
  }

  // ── CSV Import ─────────────────────────────────────────────────
  const [importing, setImporting] = useState(false);
  async function handleCSVImport(file: File) {
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { notifications.show({ message: 'CSV must have a header row and at least one data row', color: 'yellow' }); return; }
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
      let created = 0;
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const row: Record<string, string> = {};
        headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
        const orgForm: Record<string, string> = {
          orgName: row['organization'] || row['orgname'] || row['name'] || '',
          orgType: row['type'] || row['orgtype'] || 'local_government',
          orgRole: row['role'] || row['orgrole'] || 'sub_applicant',
          primaryContact: row['contact'] || row['primarycontact'] || '',
          primaryEmail: row['email'] || row['primaryemail'] || '',
          primaryPhone: row['phone'] || row['primaryphone'] || '',
          femaApplicantId: row['femaid'] || row['femaapplicantid'] || '',
          ueiNumber: row['uei'] || row['ueinumber'] || '',
          fipsCode: row['fips'] || row['fipscode'] || '',
          status: 'active',
        };
        if (!orgForm.orgName) continue;
        await createOrg(orgForm);
        created++;
      }
      notifications.show({ message: `Imported ${created} organization${created !== 1 ? 's' : ''}`, color: 'green' });
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Import failed', color: 'red' });
    } finally { setImporting(false); }
  }

  // ── Loading state ──────────────────────────────────────────────

  if (loadingOrgs && organizations.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  // ── Render ─────────────────────────────────────────────────────

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Title order={3} c="black">Applicants / Organizations</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconDownload size={16} />} onClick={() => {
            exportToCSV('pa-applicants.csv',
              ['Organization', 'Type', 'Role', 'FEMA ID', 'Primary Contact', 'Email', 'Status'],
              filtered.map(o => [o.orgName, o.orgType, o.orgRole, o.femaApplicantId, o.primaryContact, o.primaryEmail, o.status])
            );
          }}>Export CSV</Button>
          {canEdit && (
            <label style={{ cursor: 'pointer' }}>
              <Button size="xs" variant="light" leftSection={<IconUpload size={14} />} loading={importing}
                component="span">Import CSV</Button>
              <input type="file" accept=".csv" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleCSVImport(f); e.target.value = ''; }} />
            </label>
          )}
          {canEdit && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Add Organization
            </Button>
          )}
        </Group>
      </Group>

      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search name, FEMA ID, UEI, contact, city..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs"
          style={{ flex: 1, minWidth: 220 }}
        />
        <Select
          data={FILTER_TYPE_OPTIONS}
          value={typeFilter}
          onChange={v => setTypeFilter(v ?? 'all')}
          size="xs"
          w={200}
        />
        <Select
          data={FILTER_ROLE_OPTIONS}
          value={roleFilter}
          onChange={v => setRoleFilter(v ?? 'all')}
          size="xs"
          w={180}
        />
      </Group>

      <Text size="xs" c="dimmed">
        {filtered.length} organization{filtered.length !== 1 ? 's' : ''}
      </Text>

      {/* Table */}
      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('orgName')}>Organization Name{sortIcon('orgName')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('orgType')}>Type{sortIcon('orgType')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('orgRole')}>Role{sortIcon('orgRole')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('femaApplicantId')}>FEMA ID{sortIcon('femaApplicantId')}</Table.Th>
                <Table.Th>Projects</Table.Th>
                <Table.Th>Total Est. Cost</Table.Th>
                <Table.Th>Docs</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
                <Table.Th style={{ width: 80 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={9} style={{ textAlign: 'center', padding: 24 }}>
                    <Text c="dimmed">{loadingOrgs ? 'Loading...' : 'No organizations found'}</Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {pageRows.map(org => {
                const orgProjects = projects.filter(p => String(p.organizationId) === String(org.Rid));
                const orgDocs = documents.filter(d => String(d.organizationId) === String(org.Rid));
                const totalEst = orgProjects.reduce((sum, p) => sum + (parseFloat(p.estimatedCost) || 0), 0);
                return (
                  <Table.Tr key={org.id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`${routeBase}/applicants/${org.Rid}`)}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{org.orgName || '--'}</Text>
                      <Text size="xs" c="dimmed">{[org.physicalCity, org.physicalState].filter(Boolean).join(', ')}</Text>
                    </Table.Td>
                    <Table.Td style={{ textTransform: 'capitalize' }}>{org.orgType || '--'}</Table.Td>
                    <Table.Td>
                      {org.orgRole ? (
                        <Badge size="sm" variant="light" color={ROLE_COLORS[org.orgRole] ?? 'gray'}>
                          {org.orgRole}
                        </Badge>
                      ) : '--'}
                    </Table.Td>
                    <Table.Td>{org.femaApplicantId || '--'}</Table.Td>
                    <Table.Td><Text fw={600}>{orgProjects.length}</Text></Table.Td>
                    <Table.Td style={{ fontWeight: 600 }}>
                      {totalEst > 0 ? totalEst.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '--'}
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={orgDocs.length > 5 ? 'green' : orgDocs.length > 0 ? 'yellow' : 'gray'}>
                        {orgDocs.length} files
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={STATUS_COLORS[org.status] ?? 'gray'}>
                        {org.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        {canEdit && (
                          <ActionIcon size="sm" variant="subtle" color="blue" onClick={(e) => { e.stopPropagation(); openEdit(org); }}>
                            <IconEdit size={14} />
                          </ActionIcon>
                        )}
                        {canEdit && (
                          <ActionIcon size="sm" variant="subtle" color="red" onClick={(e) => { e.stopPropagation(); openDelete(org); }}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</Button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
            const p2 = start + i;
            return p2 <= totalPages ? (
              <Button key={p2} size="xs" variant={p2 === page ? 'filled' : 'subtle'} onClick={() => setPage(p2)}>{p2}</Button>
            ) : null;
          })}
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next ›</Button>
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
          <Text size="xs" c="dimmed" ml="xs">{filtered.length} total</Text>
        </Group>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingOrg ? 'Edit Organization' : 'New Organization'}
        size="xl"
      >
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
            <Tabs.Tab value="addresses">Addresses</Tabs.Tab>
            <Tabs.Tab value="contacts">Contacts</Tabs.Tab>
            {form.orgType === 'pnp' && <Tabs.Tab value="pnp">PNP</Tabs.Tab>}
            <Tabs.Tab value="financial">Financial</Tabs.Tab>
            <Tabs.Tab value="notes">Notes</Tabs.Tab>
          </Tabs.List>

          {/* ── Basic Info ──────────────────────────────────────── */}
          <Tabs.Panel value="basic" pt="md">
            <SimpleGrid cols={2} spacing="sm">
              <TextInput label="Organization Name" required
                value={form.orgName} onChange={e => setField('orgName', e.target.value)} />
              <Select label="Organization Type" data={ORG_TYPE_OPTIONS} clearable
                value={form.orgType || null} onChange={v => setField('orgType', v ?? '')} />
              <Select label="Organization Role" data={ORG_ROLE_OPTIONS} clearable
                value={form.orgRole || null} onChange={v => setField('orgRole', v ?? '')} />
              <TextInput label="FIPS Code"
                value={form.fipsCode} onChange={e => setField('fipsCode', e.target.value)} />
              <TextInput label="FEMA Applicant ID"
                value={form.femaApplicantId} onChange={e => setField('femaApplicantId', e.target.value)} />
              <TextInput label="UEI Number"
                value={form.ueiNumber} onChange={e => setField('ueiNumber', e.target.value)} />
              <Select label="SAM Registered" data={YES_NO_OPTIONS} clearable
                value={form.samRegistered || null} onChange={v => setField('samRegistered', v ?? '')} />
              <TextInput label="SAM Expiration Date" placeholder="YYYY-MM-DD"
                value={form.samExpirationDate} onChange={e => setField('samExpirationDate', e.target.value)} />
              <TextInput label="EIN / Tax ID"
                value={form.einTaxId} onChange={e => setField('einTaxId', e.target.value)} />
              <TextInput label="Congressional District"
                value={form.congressionalDistrict} onChange={e => setField('congressionalDistrict', e.target.value)} />
            </SimpleGrid>
          </Tabs.Panel>

          {/* ── Addresses ───────────────────────────────────────── */}
          <Tabs.Panel value="addresses" pt="md">
            <Text fw={600} mb="xs">Physical Address</Text>
            <SimpleGrid cols={2} spacing="sm" mb="md">
              <TextInput label="Street Address"
                value={form.physicalAddress} onChange={e => setField('physicalAddress', e.target.value)} />
              <TextInput label="City"
                value={form.physicalCity} onChange={e => setField('physicalCity', e.target.value)} />
              <TextInput label="State"
                value={form.physicalState} onChange={e => setField('physicalState', e.target.value)} />
              <TextInput label="ZIP Code"
                value={form.physicalZip} onChange={e => setField('physicalZip', e.target.value)} />
              <TextInput label="County"
                value={form.physicalCounty} onChange={e => setField('physicalCounty', e.target.value)} />
            </SimpleGrid>

            <Group mb="xs">
              <Text fw={600}>Mailing Address</Text>
              <Checkbox
                label="Same as physical"
                checked={sameAsPhysical}
                onChange={e => setSameAsPhysical(e.currentTarget.checked)}
                size="xs"
              />
            </Group>
            <SimpleGrid cols={2} spacing="sm">
              <TextInput label="Street Address" disabled={sameAsPhysical}
                value={form.mailingAddress} onChange={e => setField('mailingAddress', e.target.value)} />
              <TextInput label="City" disabled={sameAsPhysical}
                value={form.mailingCity} onChange={e => setField('mailingCity', e.target.value)} />
              <TextInput label="State" disabled={sameAsPhysical}
                value={form.mailingState} onChange={e => setField('mailingState', e.target.value)} />
              <TextInput label="ZIP Code" disabled={sameAsPhysical}
                value={form.mailingZip} onChange={e => setField('mailingZip', e.target.value)} />
            </SimpleGrid>
          </Tabs.Panel>

          {/* ── Contacts ────────────────────────────────────────── */}
          <Tabs.Panel value="contacts" pt="md">
            <Text fw={600} mb="xs">Primary Contact</Text>
            <SimpleGrid cols={2} spacing="sm" mb="md">
              <TextInput label="Name"
                value={form.primaryContact} onChange={e => setField('primaryContact', e.target.value)} />
              <TextInput label="Title"
                value={form.primaryTitle} onChange={e => setField('primaryTitle', e.target.value)} />
              <TextInput label="Email"
                value={form.primaryEmail} onChange={e => setField('primaryEmail', e.target.value)} />
              <TextInput label="Phone"
                value={form.primaryPhone} onChange={e => setField('primaryPhone', e.target.value)} />
            </SimpleGrid>

            <Text fw={600} mb="xs">Alternate Contact</Text>
            <SimpleGrid cols={2} spacing="sm" mb="md">
              <TextInput label="Name"
                value={form.altContact} onChange={e => setField('altContact', e.target.value)} />
              <TextInput label="Title"
                value={form.altTitle} onChange={e => setField('altTitle', e.target.value)} />
              <TextInput label="Email"
                value={form.altEmail} onChange={e => setField('altEmail', e.target.value)} />
              <TextInput label="Phone"
                value={form.altPhone} onChange={e => setField('altPhone', e.target.value)} />
            </SimpleGrid>

            <Text fw={600} mb="xs">Governor's Authorized Representative (GAR)</Text>
            <SimpleGrid cols={2} spacing="sm" mb="md">
              <TextInput label="Name"
                value={form.garName} onChange={e => setField('garName', e.target.value)} />
              <TextInput label="Email"
                value={form.garEmail} onChange={e => setField('garEmail', e.target.value)} />
              <TextInput label="Phone"
                value={form.garPhone} onChange={e => setField('garPhone', e.target.value)} />
            </SimpleGrid>

            <Text fw={600} mb="xs">State Coordinating Officer (SCO) / SPAO</Text>
            <SimpleGrid cols={2} spacing="sm">
              <TextInput label="SCO Name"
                value={form.scoName} onChange={e => setField('scoName', e.target.value)} />
              <TextInput label="SPAO Name"
                value={form.spaoName} onChange={e => setField('spaoName', e.target.value)} />
              <TextInput label="SPAO Email"
                value={form.spaoEmail} onChange={e => setField('spaoEmail', e.target.value)} />
            </SimpleGrid>

            <Textarea label="Authorized Portal Users" mt="md"
              description="Comma-separated email addresses of users who can access this organization's PA Grants portal"
              placeholder="user1@example.com, user2@example.com"
              value={form.contactEmails} onChange={e => setField('contactEmails', e.target.value)}
              rows={2} />
          </Tabs.Panel>

          {/* ── PNP (conditional) ────────────────────────────────── */}
          {form.orgType === 'pnp' && (
            <Tabs.Panel value="pnp" pt="md">
              <SimpleGrid cols={2} spacing="sm">
                <TextInput label="PNP Type"
                  value={form.pnpType} onChange={e => setField('pnpType', e.target.value)} />
                <Select label="Tax Exempt Verified" data={YES_NO_OPTIONS} clearable
                  value={form.pnpTaxExemptVerified || null}
                  onChange={v => setField('pnpTaxExemptVerified', v ?? '')} />
                <TextInput label="Tax Exempt Letter (reference)"
                  value={form.pnpTaxExemptLetter} onChange={e => setField('pnpTaxExemptLetter', e.target.value)} />
                <Select label="Open to Public" data={YES_NO_OPTIONS} clearable
                  value={form.pnpOpenToPublic || null}
                  onChange={v => setField('pnpOpenToPublic', v ?? '')} />
                <Textarea label="Services Description" autosize minRows={2}
                  value={form.pnpServicesDescription}
                  onChange={e => setField('pnpServicesDescription', e.target.value)} />
                <Select label="Critical Facility" data={YES_NO_OPTIONS} clearable
                  value={form.pnpCriticalFacility || null}
                  onChange={v => setField('pnpCriticalFacility', v ?? '')} />
              </SimpleGrid>
            </Tabs.Panel>
          )}

          {/* ── Financial ───────────────────────────────────────── */}
          <Tabs.Panel value="financial" pt="md">
            <SimpleGrid cols={2} spacing="sm">
              <Select label="Procurement Policy on File" data={YES_NO_OPTIONS} clearable
                value={form.procurementPolicyOnFile || null}
                onChange={v => setField('procurementPolicyOnFile', v ?? '')} />
              <Select label="Force Account Policy on File" data={YES_NO_OPTIONS} clearable
                value={form.forceAccountPolicyOnFile || null}
                onChange={v => setField('forceAccountPolicyOnFile', v ?? '')} />
              <TextInput label="Indirect Cost Rate (%)"
                value={form.indirectCostRate} onChange={e => setField('indirectCostRate', e.target.value)} />
              <Select label="Indirect Cost Rate Type" data={INDIRECT_COST_RATE_TYPE_OPTIONS} clearable
                value={form.indirectCostRateType || null}
                onChange={v => setField('indirectCostRateType', v ?? '')} />
              <Select label="Single Audit Required" data={YES_NO_OPTIONS} clearable
                value={form.singleAuditRequired || null}
                onChange={v => setField('singleAuditRequired', v ?? '')} />
              <Select label="Single Audit Current" data={YES_NO_OPTIONS} clearable
                value={form.singleAuditCurrent || null}
                onChange={v => setField('singleAuditCurrent', v ?? '')} />
              <TextInput label="Bank Name"
                value={form.bankName} onChange={e => setField('bankName', e.target.value)} />
              <TextInput label="Bank Routing Number"
                value={form.bankRoutingNumber} onChange={e => setField('bankRoutingNumber', e.target.value)} />
              <TextInput label="Bank Account Number"
                value={form.bankAccountNumber} onChange={e => setField('bankAccountNumber', e.target.value)} />
              <Select label="Bank Account Type" data={BANK_ACCOUNT_TYPE_OPTIONS} clearable
                value={form.bankAccountType || null}
                onChange={v => setField('bankAccountType', v ?? '')} />
            </SimpleGrid>
          </Tabs.Panel>

          {/* ── Notes ───────────────────────────────────────────── */}
          <Tabs.Panel value="notes" pt="md">
            <SimpleGrid cols={2} spacing="sm" mb="sm">
              <Select label="Status" data={STATUS_OPTIONS}
                value={form.status || 'active'}
                onChange={v => setField('status', v ?? 'active')} />
            </SimpleGrid>
            <Textarea label="Notes" autosize minRows={4}
              value={form.notes} onChange={e => setField('notes', e.target.value)} />
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
          {canEdit && (
            <Button loading={saving} onClick={handleSave}>
              {editingOrg ? 'Update' : 'Create'}
            </Button>
          )}
        </Group>
      </Modal>

      {/* ── Delete Confirmation Modal ────────────────────────────── */}
      <Modal
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete Organization"
        size="sm"
      >
        <Text mb="md">
          Are you sure you want to delete <strong>{deletingOrg?.orgName || 'this organization'}</strong>?
          This action cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteOpen(false)}>Cancel</Button>
          {canEdit && <Button color="red" loading={deleting} onClick={handleDelete}>Delete</Button>}
        </Group>
      </Modal>
    </Stack>
  );
}
